// Klymart — EU-conform certifications across the main brand sectors.
// Canonical id (English, stable) is what gets stored in MongoDB and exported.
// `labels` provides the user-facing name per locale. `ref` is a short EU
// regulation / scheme reference shown as a sub-line.

export const CERT_SECTORS = ['all', 'food', 'cosmetics', 'fashion', 'electronics', 'cross'];

export const SECTOR_LABELS = {
  en: {
    all: 'All sectors',
    food: 'Food',
    cosmetics: 'Cosmetics',
    fashion: 'Fashion',
    electronics: 'Electronics',
    cross: 'Cross‑sector',
  },
  de: {
    all: 'Alle Branchen',
    food: 'Lebensmittel',
    cosmetics: 'Kosmetik',
    fashion: 'Mode',
    electronics: 'Elektronik',
    cross: 'Branchenübergreifend',
  },
};

export const CERTIFICATIONS_DATA = [
  // ---------- FOOD ----------
  { id: 'EU Organic',          sector: 'food', labels: { en: 'EU Organic',           de: 'EU‑Bio‑Siegel' },        ref: 'EU 2018/848' },
  { id: 'Demeter',             sector: 'food', labels: { en: 'Demeter',              de: 'Demeter' },              ref: 'Biodynamic' },
  { id: 'Naturland',           sector: 'food', labels: { en: 'Naturland',            de: 'Naturland' },            ref: 'EU organic+' },
  { id: 'Fairtrade',           sector: 'food', labels: { en: 'Fairtrade',            de: 'Fairtrade' },            ref: 'FLO‑CERT' },
  { id: 'Rainforest Alliance', sector: 'food', labels: { en: 'Rainforest Alliance',  de: 'Rainforest Alliance' } },
  { id: 'MSC',                 sector: 'food', labels: { en: 'MSC',                  de: 'MSC' },                  ref: 'Marine Stewardship' },
  { id: 'ASC',                 sector: 'food', labels: { en: 'ASC',                  de: 'ASC' },                  ref: 'Aquaculture Stewardship' },
  { id: 'V-Label',             sector: 'food', labels: { en: 'V‑Label',              de: 'V‑Label' },              ref: 'Vegan / Vegetarian' },
  { id: 'PDO',                 sector: 'food', labels: { en: 'PDO / PGI',            de: 'g.U. / g.g.A.' },        ref: 'EU origin' },

  // ---------- COSMETICS ----------
  { id: 'COSMOS Organic',      sector: 'cosmetics', labels: { en: 'COSMOS Organic',  de: 'COSMOS Organic' },       ref: 'Natural & organic' },
  { id: 'NATRUE',              sector: 'cosmetics', labels: { en: 'NATRUE',          de: 'NATRUE' } },
  { id: 'Ecocert',             sector: 'cosmetics', labels: { en: 'Ecocert',         de: 'Ecocert' } },
  { id: 'EU Ecolabel Cosmetics', sector: 'cosmetics', labels: { en: 'EU Ecolabel',   de: 'EU Ecolabel' },          ref: 'EC 66/2010' },
  { id: 'Leaping Bunny',       sector: 'cosmetics', labels: { en: 'Leaping Bunny',   de: 'Leaping Bunny' },        ref: 'Cruelty‑free' },
  { id: 'Vegan Society',       sector: 'cosmetics', labels: { en: 'Vegan Society',   de: 'Vegan Society' } },

  // ---------- FASHION ----------
  { id: 'GOTS',                sector: 'fashion', labels: { en: 'GOTS',              de: 'GOTS' },                 ref: 'Global Organic Textile Standard' },
  { id: 'OEKO-TEX',            sector: 'fashion', labels: { en: 'OEKO‑TEX',          de: 'OEKO‑TEX' },             ref: 'Standard 100 / Made in Green' },
  { id: 'GRS',                 sector: 'fashion', labels: { en: 'GRS',               de: 'GRS' },                  ref: 'Global Recycled Standard' },
  { id: 'Cradle to Cradle',    sector: 'fashion', labels: { en: 'Cradle to Cradle',  de: 'Cradle to Cradle' } },
  { id: 'bluesign',            sector: 'fashion', labels: { en: 'bluesign',          de: 'bluesign' } },
  { id: 'Fair Wear',           sector: 'fashion', labels: { en: 'Fair Wear',         de: 'Fair Wear' } },
  { id: 'Leather Working Group', sector: 'fashion', labels: { en: 'Leather Working Group', de: 'Leather Working Group' } },
  { id: 'Responsible Wool Standard', sector: 'fashion', labels: { en: 'Responsible Wool', de: 'Responsible Wool' } },

  // ---------- ELECTRONICS ----------
  { id: 'EU Ecolabel Electronics', sector: 'electronics', labels: { en: 'EU Ecolabel', de: 'EU Ecolabel' },        ref: 'EC 66/2010' },
  { id: 'EPEAT',               sector: 'electronics', labels: { en: 'EPEAT',          de: 'EPEAT' } },
  { id: 'TCO Certified',       sector: 'electronics', labels: { en: 'TCO Certified',  de: 'TCO Certified' } },
  { id: 'Blue Angel',          sector: 'electronics', labels: { en: 'Blue Angel',     de: 'Blauer Engel' } },
  { id: 'Energy Star',         sector: 'electronics', labels: { en: 'Energy Star',    de: 'Energy Star' } },
  { id: 'RoHS',                sector: 'electronics', labels: { en: 'RoHS',           de: 'RoHS' },                ref: 'EU 2011/65' },
  { id: 'WEEE',                sector: 'electronics', labels: { en: 'WEEE',           de: 'WEEE' },                ref: 'EU 2012/19' },
  { id: 'Right to Repair',     sector: 'electronics', labels: { en: 'Right to Repair', de: 'Right to Repair' },    ref: 'EU 2023/1670' },

  // ---------- CROSS-SECTOR ----------
  { id: 'B Corp',              sector: 'cross', labels: { en: 'B Corp',              de: 'B Corp' } },
  { id: 'ISO 14001',           sector: 'cross', labels: { en: 'ISO 14001',           de: 'ISO 14001' },            ref: 'Env. management' },
  { id: 'ISO 14064',           sector: 'cross', labels: { en: 'ISO 14064',           de: 'ISO 14064' },            ref: 'GHG accounting' },
  { id: 'Carbon Neutral',      sector: 'cross', labels: { en: 'Carbon Neutral',      de: 'Klimaneutral' } },
  { id: 'EU PEF',              sector: 'cross', labels: { en: 'EU PEF',              de: 'EU PEF' },               ref: 'Product Env. Footprint' },
  { id: 'EU Taxonomy',         sector: 'cross', labels: { en: 'EU Taxonomy',         de: 'EU Taxonomie' } },
  { id: 'CSRD',                sector: 'cross', labels: { en: 'CSRD ready',          de: 'CSRD‑bereit' },          ref: 'EU 2022/2464' },
  { id: 'Other',               sector: 'cross', labels: { en: 'Other',               de: 'Sonstiges' } },
];

// Convenience: lookup map by id
export const CERTIFICATIONS_BY_ID = Object.fromEntries(
  CERTIFICATIONS_DATA.map((c) => [c.id, c]),
);

export function localLabel(cert, locale) {
  return (cert.labels && (cert.labels[locale] || cert.labels.en)) || cert.id;
}
